import FileSaver from 'file-saver';
import moment from 'moment';
import deepmerge from 'deepmerge';
import {
  delay,
} from 'redux-saga/effects';
import ApolloClient from 'apollo-boost';
import { errorCodes } from '../models';


class BaseApi {
  constructor(apiHost, optionProvider, requestOptions, brand, clientType) {
    if (typeof optionProvider !== 'function') {
      throw Error('optionProvider must be a function');
    }

    this.apiHost = apiHost;
    this.optionProvider = optionProvider;
    this.requestOptions = requestOptions || {};
    this.requestOptionProviders = [];
    this.brand = brand || 'smorder';
    this.clientType = clientType;
  }

  contentTypeHeaders(type) {
    const contentType = !type ? 'application/json' : type;
    return {
      headers: {
        'Content-Type': contentType,
        Accept: contentType,
      },
    };
  }

  addRequestOptionProvider(provider) {
    this.requestOptionProviders.push(provider);
  }

  * getAccessToken() {
    const { tokenProvider, tokenRefreshTask } = yield this.optionProvider();
    const { accessToken, expiresAt } = yield tokenProvider();

    const expires = moment.utc(expiresAt);
    const now = moment.utc();

    if (now.isAfter(expires)) {
      yield tokenRefreshTask();
      return (yield tokenProvider()).accessToken;
    }

    return accessToken;
  }

  * authHeaders() {
    let accessToken = yield this.getAccessToken();

    if (!accessToken) {
      yield delay(2000);
      accessToken = yield this.getAccessToken();
    }

    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  }

  smorderAppHeaders() {
    return {
      headers: {
        'x-smorder-app-name': this.brand,
        ...(this.clientType && { 'x-smorder-client-type': this.clientType }),
      },
    };
  }

  * apiFetch(path, options = {}, signal) {
    const smorderAppHeader = this.smorderAppHeaders();
    const contentTypeHeader = deepmerge(smorderAppHeader, this.contentTypeHeaders());
    let newOptions = deepmerge(deepmerge(contentTypeHeader, this.requestOptions), options);

    for (let i = 0; i < this.requestOptionProviders.length; i++) {
      newOptions = deepmerge(newOptions, yield this.requestOptionProviders[i]());
    }

    if (options.isFormData) {
      delete newOptions.headers['Content-Type'];
      newOptions.body = options.body;
    }

    try {
      const response = yield fetch([options.apiHost || this.apiHost, path].join(''), { ...newOptions, signal });

      if (!response.ok) {
        try {
          const responsePayload = yield response.json();
          return yield { errorCode: response.status, response, responsePayload };
        } catch {
          return yield { errorCode: response.status, response };
        }
      }

      // http 204 = no content, causes error on .json() call
      if (newOptions.parseAsText || response.status === 204) {
        return yield response.text();
      }

      try {
        return yield response.json();
      } catch {
        return yield { errorCode: errorCodes.jsonDeserializationError, response };
      }
    } catch {
      return yield { errorCode: errorCodes.networkError };
    }
  }

  * authorizedFetch(url, options = {}, signal) {
    const authHeaders = yield this.authHeaders();
    const contentTypeHeaders = this.contentTypeHeaders(options.contentType);
    const newOptions = deepmerge(deepmerge(authHeaders, contentTypeHeaders), options);

    if (options.isFormData) {
      newOptions.body = options.body;
    }

    return yield this.apiFetch(
      url,
      newOptions,
      signal,
    );
  }

  * apolloClient(uri) {
    const auth = yield this.authHeaders();


    let options = {};
    for (let i = 0; i < this.requestOptionProviders.length; i++) {
      options = deepmerge(options, yield this.requestOptionProviders[i]());
    }

    return new ApolloClient({
      uri: `${this.apiHost}${uri}`,
      headers: {
        ...auth.headers,
        ...(options.headers || {}),
      },
    });
  }

  * authorizedGraphQLFetch(query, uri = '/basedataservice/api/v1/graphql/') {
    const client = yield this.apolloClient(uri);
    return yield client
      .query({
        query,
      });
  }

  * authorizedDownload(url, options) {
    const authHeaders = yield this.authHeaders(options.contentType);
    const contentTypeHeaders = this.contentTypeHeaders(options.contentType);
    const newOptions = deepmerge(deepmerge(authHeaders, contentTypeHeaders), options);

    return yield this.Download(url, newOptions);
  }

  * Download(url, options) {
    let newOptions = options;

    if (!options.isFormData) {
      for (let i = 0; i < this.requestOptionProviders.length; i++) {
        newOptions = deepmerge(newOptions, yield this.requestOptionProviders[i]());
      }
    }


    const response = yield fetch([this.apiHost, url].join(''), newOptions);
    const contentDispositionHeader = response.headers.get('content-disposition');
    let fileNameFromServer = contentDispositionHeader ? contentDispositionHeader.split('filename=')[1] : null;
    if (fileNameFromServer) {
      fileNameFromServer = `${moment().format('YYYY-MM-DD HH-mm')}_${fileNameFromServer}`;
    }

    if (response.status === 200) {
      return yield response.blob().then((b) => {
        const blob = new Blob([b], { type: `${options.contentType};charset=utf-8` }); //eslint-disable-line
        FileSaver.saveAs(blob, fileNameFromServer || options.filename);
      });
    }
    return null;
  }

  toQueryString(params) {
    const esc = encodeURIComponent;
    return Object
      .keys(params)
      .map(k => `${esc(k)}=${esc(params[k])}`)
      .join('&');
  }
}

export default BaseApi;
